import { BUNDLED } from './registry';
import { hasRole } from '../session';
import type { BundledModule } from './registry-types';

/**
 * What the signed-in user may actually open.
 *
 * Filtered by exact role, so a link is never rendered for a view the API would
 * refuse. This is cosmetic — hiding a link stops nobody — and the API check is
 * the real one. What it buys is a dashboard that does not advertise what it
 * will not do.
 */

export interface NavLink {
	/** Absolute, already mounted under the module: `/m/ses/credentials`. */
	readonly to: string;
	readonly title: string;
}

export interface NavSection {
	readonly moduleId: string;
	readonly title: string;
	readonly links: readonly NavLink[];
}

export function modulePath(moduleId: string, path: string): string {
	return path === '' ? `/m/${moduleId}` : `/m/${moduleId}/${path}`;
}

/**
 * The core's own section. Not a module — it is present in every deployment and
 * its routes are not mounted under `/m/` — but it is filtered by role through
 * exactly the same rule, so an admin link never appears for someone who is not
 * one.
 */
const CORE_LINKS: readonly (NavLink & { readonly requiredRole: string })[] = [
	{ to: '/admin/users', title: 'Users', requiredRole: 'core:admin' },
	{ to: '/admin/audit', title: 'Audit log', requiredRole: 'core:admin' },
	{ to: '/admin/settings', title: 'Settings', requiredRole: 'core:admin' },
	{ to: '/admin/security', title: 'Security', requiredRole: 'core:admin' },
];

export function navSections(modules: readonly BundledModule[] = BUNDLED): NavSection[] {
	const sections: NavSection[] = [];

	const coreLinks = CORE_LINKS.filter((link) => hasRole(link.requiredRole)).map(
		({ to, title }) => ({ to, title }),
	);
	if (coreLinks.length > 0) {
		sections.push({ moduleId: 'core', title: 'Administration', links: coreLinks });
	}

	for (const { manifest } of modules) {
		const links = manifest.nav
			.filter((entry) => hasRole(entry.requiredRole))
			.map((entry) => ({ to: modulePath(manifest.id, entry.path), title: entry.title }));

		// A module every one of whose entries is hidden contributes no heading
		// either, rather than an empty section implying something is missing.
		if (links.length > 0) {
			sections.push({ moduleId: manifest.id, title: manifest.title, links });
		}
	}

	return sections;
}
